import MelodySidebar from '@/context/domain/g02/g02-d03/g02-d03-p001-karaoke/components/MelodySidebar';
import KaraokeSidebar from '@/context/domain/g02/g02-d03/g02-d03-p001-karaoke/components/KaraokeSidebar';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import HeaderKaraoke from '@/context/domain/g02/g02-d03/g02-d03-p001-karaoke/components/Header/Index';
import { redirect, RedirectType } from 'next/navigation';
import Player from '@/context/domain/g02/g02-d03/g02-d03-p001-karaoke/components/Player';
import EmptyQueue from '@/context/domain/g02/g02-d03/g02-d03-p001-karaoke/components/EmptyQueue';
import { useKaraokeStore } from '@/context/global/stores/karaokeStore';
import { useSettingsStore } from '@/context/global/stores/settingStore';
import { useAuthStore } from '@/context/global/stores/authStore';
import { useSetSongFollow } from '@/context/global/hooks/post/useSetSongFollow';
import { useGetOAuth } from '@/context/global/hooks/auth/useGetOAuth';
import SongAddedToast from '@/context/domain/g02/g02-d03/g02-d03-p001-karaoke/components/SongAddedToast';
import { useFetchSongDetail } from '@/context/global/hooks/song';
import { useQueueToast } from '@/context/global/hooks/useQueueToast';
import ModalPackageUpgrade from '@/context/global/components/g02/g02-d03/g02-d03-c001-modal-package-upgrade';
import CWSpinner from '@/context/global/components/g00/g00-d00-g005-cw-spinner';
import CWPopup from '@/context/global/components/g00/g00-d00-g007-cw-popup';
import { isAxiosError } from 'axios';
import { TApiSuccessResponse } from '@/context/global/types/api';
import { TSongDetailRes } from '@/context/global/api/helper/song';
import ErrorSuspense from '@/context/domain/g02/g02-d03/g02-d03-p001-karaoke/components/Error/ErrorSuspense';
import ErrorFreeSongLimit from '@/context/domain/g02/g02-d03/g02-d03-p001-karaoke/components/Error/ErrorFreeSongLimit';
import ErrorNoPackage from '@/context/domain/g02/g02-d03/g02-d03-p001-karaoke/components/Error/ErrorNoPackage';
import ErrorVideoPlayer from '@/context/domain/g02/g02-d03/g02-d03-p001-karaoke/components/Error/ErrorVideoPlayer';
import ErrorGeneric from '@/context/global/components/g00/g00-d00-g018-cw-error-generic';
import { cn } from '@/context/global/utils/cn';
import { useSessionHeartbeat } from '@/context/domain/g02/g02-d03/g02-d03-p001-karaoke/hooks/useSessionHeartbeat';
import { useTranslations } from 'next-intl';
import { CWExitKaraokeConfirm } from '@/context/global/components/g00/g00-d00-g021-cw-exit-karaoke-confirm';

const KaraokePage = () => {
  const t = useTranslations('SessionHeartbeat');
  const { isKaraoke, setIsKaraoke, volume, setVolume } = useSettingsStore();
  const { user } = useAuthStore();

  const [isMusicManagerOpen, setIsMusicManagerOpen] = useState(false);
  const [isMelodyOpen, setIsMelodyOpen] = useState(false);

  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [isMobileLandscape, setIsMobileLandscape] = useState(false);

  // Auto-close sidebar when mobile rotates to landscape
  useEffect(() => {
    const mql = window.matchMedia('(orientation: landscape) and (max-height: 500px)');

    const handleOrientationChange = (e: MediaQueryListEvent | MediaQueryList) => {
      setIsMobileLandscape(e.matches);
      if (e.matches) {
        setIsMusicManagerOpen(false);
        setIsMelodyOpen(false);
        setIsSearchOpen(false);
      }
    };

    // Check on mount
    handleOrientationChange(mql);

    mql.addEventListener('change', handleOrientationChange);
    return () => mql.removeEventListener('change', handleOrientationChange);
  }, []);

  // Local state for song detail handling
  const [videoSource, setVideoSource] = useState<string | null>(null);
  const [isLoadingVideo, setIsLoadingVideo] = useState(false);
  const [isFavorite, setIsFavorite] = useState(false);
  const [isOpenUpgradePackage, setIsOpenUpgradePackage] = useState(false);
  const [openUpgradePackageMsg, setOpenUpgradePackageMsg] = useState<string | undefined>(
    undefined,
  );
  const [showExitConfirm, setShowExitConfirm] = useState(false);

  // Error state for different API error codes
  const [errorState, setErrorState] = useState<{
    type:
      | 'suspended'
      | 'freeSongLimit'
      | 'noPackage'
      | 'videoError'
      | 'genericError'
      | null;
    message?: string;
  }>({ type: null });

  const [heartbeatEnabled, setHeartbeatEnabled] = useState(true);
  const [isPlaying, setIsPlaying] = useState(false);
  const { concurrentSessionError, clearConcurrentSessionError } = useSessionHeartbeat({
    enabled: heartbeatEnabled && isPlaying,
  });

  const store = useKaraokeStore();
  const hasAutoOpened = useRef(false);
  const sidebarRef = useRef<HTMLDivElement>(null);

  const isPreview = useMemo(() => {
    // badge=0 + เพลงไม่ฟรี (conditions !== 0) preview mode
    // badge=0 + เพลงฟรี (conditions === 0)  เล่นเต็มเพลง
    return user?.badge == 0 && store.currentSong?.conditions !== 0;
  }, [user?.badge, store.currentSong?.conditions]);

  // Auto-open sidebar when there is a current song or songs in queue
  useEffect(() => {
    const isLandscape = window.matchMedia(
      '(orientation: landscape) and (max-height: 500px)',
    );

    if (
      (store.currentSong || store.queue.length > 0) &&
      !isLandscape.matches &&
      !hasAutoOpened.current &&
      !isPreview
    ) {
      setIsMusicManagerOpen(true);
      hasAutoOpened.current = true;
    }

    // Reset when no song playing and queue is empty
    if (!store.currentSong && store.queue.length === 0) {
      hasAutoOpened.current = false;
    }
  }, [store.currentSong, store.queue.length, isPreview]);

  // Auto-scroll to search panel on mobile
  useEffect(() => {
    if (isSearchOpen && isMusicManagerOpen) {
      const isMobile = window.innerWidth < 768;
      if (isMobile) {
        setTimeout(() => {
          sidebarRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }, 100);
      }
    }
  }, [isSearchOpen, isMusicManagerOpen]);

  // หยุดเล่นเพลงถ้าจาก heartbeat (603) + clear session + หยุด heartbeat
  useEffect(() => {
    if (concurrentSessionError) {
      setVideoSource(null);
      useKaraokeStore.getState().setSessionId(null);
      setHeartbeatEnabled(false);
    }
  }, [concurrentSessionError]);

  const { fetchSongDetail, isLoading: isVideoLinkFetching } =
    useFetchSongDetail(isKaraoke);

  const { setSongFollow } = useSetSongFollow();
  const { fetchOAuthUrl } = useGetOAuth();
  const { toastSong, showToast } = useQueueToast();

  const [resumeTime, setResumeTime] = useState(0);

  const prevSongIdRef = useRef<number | null>(null);
  const fetchIdRef = useRef(0);

  const handleFetchSong = useCallback(async () => {
    if (!store.currentSong) {
      return;
    }

    // ป้องกัน race condition: ignore stale response จาก fetch ก่อนหน้า
    const currentFetchId = ++fetchIdRef.current;

    // Reset error state ก่อน fetch ใหม่
    setErrorState({ type: null });

    // Only reset resume time when song actually changes (not vocal toggle)
    if (prevSongIdRef.current !== store.currentSong.id) {
      setResumeTime(0);
      setVideoSource(null);
      prevSongIdRef.current = store.currentSong.id;
    }

    setIsLoadingVideo(true);

    try {
      const res = await fetchSongDetail(store.currentSong.id);

      // ถ้ามี fetch ใหม่กว่าแล้ว → ignore response นี้ (stale)
      if (fetchIdRef.current !== currentFetchId) return;

      const { responseObject: song, message } = res.data;

      // Set favorite status from response
      setIsFavorite(song.IsfavoriteSong ?? false);

      // Badge logic:
      // badge: 0 = No Package, 1 = Free, 2 = Platinum, 3 = Silver
      // conditions: 0 = free, 2 = silver, 3 = platinum
      if (user?.badge === 2) {
        // Platinum user - can access all songs
      } else if (user?.badge === 3) {
        // Silver user - can access platinum and free songs
        if (song.conditions === 2) {
          // Song requires silver - lock for silver users
          setOpenUpgradePackageMsg(message);
          setIsOpenUpgradePackage(true);
          setIsLoadingVideo(false);
          return;
        }
      } else if (user?.badge === 1) {
        // Free user - can access free songs only
        if (song.conditions === 2 || song.conditions === 3) {
          // Song requires silver or platinum
          setOpenUpgradePackageMsg(message);
          setIsOpenUpgradePackage(true);
          setIsLoadingVideo(false);
          return;
        }
      } else {
        // No package (badge === 0) - demo mode
      }

      // Set video source
      // Force reload by resetting to null first, then setting new source after a tick
      // This ensures Player component properly re-mounts when toggling Karaoke/Vocal mode
      const newSource = song.link ?? null;

      if (videoSource === newSource && prevSongIdRef.current === store.currentSong.id) {
        // Same URL but vocal mode changed - force reload
        console.log('[Karaoke] Forcing video reload for vocal toggle');
        setVideoSource(null);
        setTimeout(() => {
          setVideoSource(newSource);
          setIsLoadingVideo(false);
        }, 0);
      } else {
        setVideoSource(newSource);
        setIsLoadingVideo(false);
      }

      store.setSessionId(res.data.responseObject.session);
    } catch (error) {
      // ถ้ามี fetch ใหม่กว่าแล้ว → ignore error นี้ (stale)
      if (fetchIdRef.current !== currentFetchId) return;

      if (!isAxiosError(error)) {
        // Handle non-axios errors (network errors, etc.)
        console.error('[Karaoke] Non-axios error:', error);
        setVideoSource(null);
        setErrorState({ type: 'genericError' });
        setIsLoadingVideo(false);
        return;
      }

      const res: TApiSuccessResponse<TSongDetailRes> = error.response?.data;

      const code = error.response?.data?.code;
      const httpStatus = error.response?.status;
      console.error('[Karaoke] API error:', { code, httpStatus, message: res?.message });

      // 410: suspended (บช ถูกระงับ / ไม่จ่ายเงิน)
      // 411: คุณร้องเพลงฟรีครบ 3 เพลงแล้ว
      // 412: ยังไม่มีแพ็กเกจ
      // 417: ยังไม่มีแพ็กเกจ Platinum

      if (code === 410) {
        setVideoSource(null);
        setErrorState({ type: 'suspended', message: res.message });
        setIsLoadingVideo(false);
        return;
      }

      if (code === 411) {
        setVideoSource(null);
        setErrorState({ type: 'freeSongLimit', message: res.message });
        setIsLoadingVideo(false);
        return;
      }

      if (code === 412) {
        setVideoSource(res.responseObject.link ?? null);
        setErrorState({ type: 'noPackage', message: res.message });
        setIsLoadingVideo(false);
        return;
      }

      if (code === 417) {
        setOpenUpgradePackageMsg(res.message);
        setIsOpenUpgradePackage(true);
        setIsLoadingVideo(false);
        return;
      }

      // Catch-all for unhandled API errors
      setVideoSource(null);
      setErrorState({ type: 'genericError', message: res?.message });
      setIsLoadingVideo(false);
    }

    return () => {
      store.setSessionId(null);
    };
  }, [fetchSongDetail, store, user]);

  useEffect(() => {
    handleFetchSong();
  }, [store.currentSong?.id, isKaraoke, user?.badge]);

  const handleFavorite = useCallback(async () => {
    if (!store.currentSong) return;
    const newValue = !isFavorite;
    const success = await setSongFollow(store.currentSong.id, newValue);
    if (success) {
      setIsFavorite(newValue);
    }
  }, [store.currentSong, isFavorite, setSongFollow, setIsFavorite]);

  const handleNext = useCallback(() => {
    store.setCurrentSong(null);
    store.setSessionId(null);
    setVideoSource(null);

    // Capture current queue state before setTimeout
    const currentQueue = store.queue;

    if (currentQueue.length > 0) {
      // Wait for cleanup before loading next song
      setTimeout(() => {
        // Re-check queue length to prevent playing after clearQueue
        const latestQueue = useKaraokeStore.getState().queue;
        if (latestQueue.length > 0) {
          store.setCurrentSong(latestQueue[0]);
          store.removeByIndex(0);
        }
      }, 10);
    }
  }, [store]);

  const handleDemoEnded = useCallback(() => {
    store.setCurrentSong(null);
    redirect('/karaoke/continue', RedirectType.push);
  }, [store]);

  return (
    <>
      {/* Error overlays - show when error state is set */}
      {errorState.type === 'suspended' && (
        <ErrorSuspense
          message={errorState.message}
          onClickBackToLogin={async () => {
            const oauthUrl = await fetchOAuthUrl();
            if (oauthUrl) {
              window.location.href = oauthUrl;
            } else {
              redirect('/', RedirectType.push);
            }
          }}
        />
      )}
      {errorState.type === 'freeSongLimit' && (
        <ErrorFreeSongLimit
          message={errorState.message}
          onClickRetry={() => {
            setErrorState({ type: null });
            handleFetchSong();
          }}
          onClickNextSong={() => {
            setErrorState({ type: null });
            handleNext();
          }}
        />
      )}
      {errorState.type === 'noPackage' && (
        <ErrorNoPackage
          message={errorState.message}
          onClickTrySong={() => setErrorState({ type: null })}
          onClickBuyPackage={() =>
            window.open(
              'https://www.ais.th/consumers/entertainment/music/karaoke#package-platinum',
              '_blank',
            )
          }
        />
      )}
      {errorState.type === 'videoError' && (
        <ErrorVideoPlayer
          message={errorState.message}
          onClickOk={() => {
            setErrorState({ type: null });
            handleNext();
          }}
        />
      )}
      {errorState.type === 'genericError' && (
        <ErrorGeneric
          message={errorState.message}
          onClickOk={() => {
            setErrorState({ type: null });
            handleNext();
          }}
        />
      )}

      {/* Main content - hidden when error is showing */}
      <main
        className={cn(
          'flex h-full w-screen flex-col md:flex-row md:overflow-hidden',
          isMusicManagerOpen || isMelodyOpen ? 'overflow-y-auto' : 'overflow-hidden',
          errorState.type && 'hidden',
        )}
      >
        <div className="relative flex aspect-video w-full md:aspect-auto">
          <HeaderKaraoke
            className={cn(
              'absolute left-0 right-0 top-0 z-50 hidden md:flex',
              isMobileLandscape && '!hidden',
            )}
            isOpen={isMelodyOpen || isMusicManagerOpen}
            isPreview={isPreview}
            onSidebarClick={() => {
              setIsMusicManagerOpen(true);
              setIsSearchOpen(false);
            }}
            onSearch={() => {
              setIsMusicManagerOpen(true);
              setIsSearchOpen(true);
            }}
            onBack={() => setShowExitConfirm(true)}
          />

          {store.currentSong ? (
            isLoadingVideo ? (
              <div className="flex h-full w-full items-center justify-center">
                <CWSpinner />
              </div>
            ) : !videoSource ? (
              <ErrorVideoPlayer
                onClickOk={() => {
                  handleNext();
                }}
              />
            ) : (
              <Player
                src={videoSource}
                hideCallingMelody={
                  !store.currentSong.callingMelodyUrl &&
                  !store.currentSong.vdoCallingMelodyUrl
                }
                songId={store.currentSong.id}
                title={store.currentSong.title}
                artist={store.currentSong.artist || undefined}
                coverUrl={store.currentSong.coverUrl || undefined}
                onMelody={() => {
                  setIsMelodyOpen(true);
                  setIsMusicManagerOpen(false);
                }}
                isVocalOn={isKaraoke}
                onVocalChange={isPreview ? undefined : (value, currentTime) => {
                  setResumeTime(currentTime);
                  setIsKaraoke(value);
                }}
                startTime={resumeTime}
                volume={volume}
                onVolumeChange={setVolume}
                isFavorite={isFavorite}
                onFavorite={handleFavorite}
                onNext={handleNext}
                disabledNext={store.queue.length === 0}
                onEnded={() => {
                  setResumeTime(0);
                  handleNext();
                }}
                onError={() => {
                  setErrorState({ type: 'videoError' });
                }}
                onSearchToggle={() => {
                  // Exit native fullscreen if active
                  if (document.fullscreenElement) {
                    document.exitFullscreen?.();
                  }
                  // eslint-disable-next-line @typescript-eslint/no-explicit-any
                  (screen.orientation as any)?.lock?.('portrait').catch(() => {});
                  setIsSearchOpen(true);
                  setIsMusicManagerOpen(true);
                }}
                isPreview={isPreview}
                disabledSeek={isPreview}
                onPreviewEnded={handleDemoEnded}
                onPlayingChange={setIsPlaying}
              />
            )
          ) : (
            <EmptyQueue
              onAddSong={() => {
                setIsMusicManagerOpen(true);
                setIsSearchOpen(true);
              }}
            />
          )}
        </div>

        {/* sidebar */}
        <div
          ref={sidebarRef}
          hidden={isPreview || !(isMelodyOpen || isMusicManagerOpen)}
          className="h-[calc(100vh-64px)] w-full transition-transform duration-300 ease-in-out md:h-screen md:max-w-[350px] xl:max-w-[500px]"
        >
          {/* {process.env.NEXT_PUBLIC_DEBUG_MODE == 'true' && <DebugKaraokeMenu />} */}
          {isMusicManagerOpen && (
            <KaraokeSidebar
              isOpen={isMusicManagerOpen}
              currentSong={store.currentSong}
              playlist={store.queue}
              isPlaying={false}
              onClose={() => {
                setIsMusicManagerOpen(false);
              }}
              onClearQueue={() => {
                store.clearQueue();
              }}
              isSearchOpen={isSearchOpen}
              setIsSearchOpen={(isOpen) => {
                setIsSearchOpen(isOpen);
              }}
              onSongSelect={(index) => {
                store.setCurrentSong(null);
                setTimeout(() => {
                  // Re-check queue to prevent race condition
                  const latestQueue = useKaraokeStore.getState().queue;
                  if (latestQueue.length > index) {
                    store.setCurrentSong(latestQueue[index]);
                    store.removeByIndex(index);
                  }
                }, 10);
              }}
              onMoveToTop={(index) => {
                store.moveToFirstQueueByIndex(index);
              }}
              onSongRemove={(index) => {
                store.removeByIndex(index);
              }}
              onAddToQueue={showToast}
            />
          )}
          {isMelodyOpen && (
            <MelodySidebar isOpen={isMelodyOpen} onClose={() => setIsMelodyOpen(false)} />
          )}
        </div>

        {/* Song Added Toast */}
        <SongAddedToast songTitle={toastSong?.title || null} />

        <ModalPackageUpgrade
          hideNextSong={store.queue.length == 0}
          message={openUpgradePackageMsg}
          isOpen={isOpenUpgradePackage}
          onClose={() => {
            setIsOpenUpgradePackage(false);
          }}
          onRetry={() => {
            handleFetchSong();
            setIsOpenUpgradePackage(false);
          }}
          onNextSong={() => {
            handleNext();
            setIsOpenUpgradePackage(false);
          }}
        />

        {/* Concurrent Session Error Modal (603) */}
        <CWPopup
          isOpen={concurrentSessionError}
          title={`603 : ${t('concurrentSessionTitle')}`}
          leftButtonText={t('goToHome')}
          onLeftButtonClick={() => redirect('/', RedirectType.push)}
          rightButtonText={t('tryAgain')}
          onRightButtonClick={() => {
            clearConcurrentSessionError();
            setHeartbeatEnabled(true);
            handleFetchSong();
          }}
          hideCloseButton={true}
        />

        {/* Exit Karaoke Confirmation Modal */}
        <CWExitKaraokeConfirm
          isOpen={showExitConfirm}
          onClose={() => setShowExitConfirm(false)}
          onConfirm={() => redirect('/', RedirectType.push)}
        />
      </main>
    </>
  );
};

export default KaraokePage;
